import { customerOrderSettings } from '../../lib/utils/customerOrderSettings';
import { useSettingsStore } from '../../lib/stores/settingsStore';
import { usePermissions } from '../../lib/hooks/usePermissions';
/**
 * OrderProductSelector
 * Component for selecting products in manual order creation
 * Shows product catalog with availability and allows quantity selection
 * Supports zone selection for stock picking
 */

import React, { useState, useEffect, useRef } from 'react';
import { Search, X, Package, AlertTriangle, Plus, Minus, MapPin } from 'lucide-react';
import toast from 'react-hot-toast';
import { supabase } from '../../lib/supabase';
import type { OrderItemInput, ProductAvailability } from '../../lib/types/orders';
import { useCommercialNameVisible } from '../../lib/hooks/useProductCommercialNames';
import { CommercialNameLine } from './ui/CommercialNameLine';
import { fetchInChunks } from '../../lib/utils/inChunks';
import { formatQuantity } from '../../lib/utils/quantityFormat';
import { resolveProductUnit } from '../../lib/constants/units';
import { formatWarehouseName } from '../../lib/utils/warehouseLabel';
import { isCustomOrderLine } from '../../lib/orders/customOrderLine';
import { CustomOrderLineForm } from './orders/CustomOrderLineForm';

interface ProductZoneStock {
  zone_id: string;
  zone_name: string;
  zone_code: string;
  quantity: number;
}

interface Product {
  id: string;
  name: string;
  sku: string;
  commercial_name?: string | null;
  stock_quantity: number;
  reserved_quantity: number; // Somma prenotazioni attive (stock_reservations), per allinearsi al check finale RPC
  manage_stock: boolean;
  cost_price: number;
  vat_rate: number | null;
  price: number; // Prezzo dal listino effettivo (o base prodotto come fallback)
  sale_unit?: string | null;
  /** UDM 1 (giacenze e zone sono in questa unità) */
  unit_of_measure_1?: string | null;
  pack_weight_kg?: number | null;
  missing_price?: boolean;
  zones: ProductZoneStock[]; // Stock per zone
}

interface OrderProductSelectorProps {
  organizationId: string;
  /** Listino da usare per i prezzi (es. listino predefinito del cliente).
   *  null sceglie esplicitamente i prezzi base; undefined usa il listino is_default della org; altrimenti il prezzo base prodotto. */
  priceListId?: string | null;
  onPriceListResolved?: (id: string | null) => void;
  selectedItems: OrderItemInput[];
  onAddItem: (item: OrderItemInput) => void;
  onUpdateItem: (index: number, updates: Partial<OrderItemInput>) => void;
  onRemoveItem: (index: number) => void;
  availabilityResults?: ProductAvailability[];
  disabled?: boolean;
}

export function OrderProductSelector({
  organizationId,
  priceListId,
  onPriceListResolved,
  selectedItems,
  onAddItem,
  onUpdateItem,
  onRemoveItem,
  availabilityResults = [],
  disabled = false
}: OrderProductSelectorProps) {
  const orderSettings = customerOrderSettings(useSettingsStore(s => s.settings));
  const { hasPermission } = usePermissions();
  const canChangePrice = !orderSettings.enabled || (orderSettings.allow_price_override && hasPermission('inventory', 'update'));
  const [products, setProducts] = useState<Product[]>([]);
  // Nome commerciale accanto al nome magazzino (solo org con «Nome commerciale» attivo)
  const showCommercialName = useCommercialNameVisible();
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showProductList, setShowProductList] = useState(false);
  const [showZoneSelector, setShowZoneSelector] = useState(false);
  const [selectedProductForZone, setSelectedProductForZone] = useState<Product | null>(null);
  const [surplusContext, setSurplusContext] = useState<{
    product: Product;
    surplusQuantity: number;
    excludeZoneIds: string[];
  } | null>(null);
  const searchRef = useRef<HTMLDivElement>(null);

  // Fast lookup: product by ID
  const productsMap = React.useMemo(() => {
    const map = new Map<string, Product>();
    products.forEach(p => map.set(p.id, p));
    return map;
  }, [products]);

  // Load products with prices from the effective price list and zone stock
  useEffect(() => {
    let cancelled = false;
    const loadProducts = async () => {
      try {
        setLoading(true);

        // Resolve effective price list: explicit prop → org default list
        let effectivePriceListId = priceListId || null;
        if (priceListId === undefined) {
          const { data: defaultList, error: defaultListError } = await supabase
            .from('price_lists')
            .select('id')
            .eq('organization_id', organizationId)
            .eq('is_default', true)
            .eq('is_active', true)
            .limit(1)
            .maybeSingle();
          if (defaultListError) throw defaultListError;
          effectivePriceListId = defaultList?.id || null;
        }

        if (cancelled) return;
        onPriceListResolved?.(effectivePriceListId);
        // Load products
        const { data: productsData, error: productsError } = await supabase
          .from('products')
          .select(`
            id,
            name,
            sku,
            commercial_name,
            stock_quantity,
            manage_stock,
            cost_price,
            regular_price,
            sale_price,
            vat_rate, unit_of_measure_1, order_sale_unit, order_pack_weight_kg
          `)
          .eq('organization_id', organizationId)
          .eq('status', 'active')
          .order('name');

        if (productsError) throw productsError;

        // Get prices from the effective price list for all products
        const priceMap = new Map<string, number>();
        if (effectivePriceListId && productsData) {
          const productIds = productsData.map(p => p.id);
          const prices = await fetchInChunks<{ product_id: string; regular_price: number | null; sale_price: number | null }>(
            productIds,
            (chunk) => supabase
              .from('product_price_list_prices')
              .select('product_id, regular_price, sale_price')
              .eq('price_list_id', effectivePriceListId)
              .in('product_id', chunk),
          );
          prices.forEach(p => {
            // Use sale_price if available, otherwise regular_price
            priceMap.set(p.product_id, p.sale_price ?? p.regular_price ?? 0);
          });
        }

        // Get active reservations for all products — la stessa base usata da get_product_available_stock,
        // così il picker mostra la stessa disponibilità netta che verrà poi verificata allo step Riepilogo.
        const reservedMap = new Map<string, number>();
        if (productsData) {
          const productIds = productsData.map(p => p.id);
          const reservations = await fetchInChunks<{ product_id: string; quantity: number }>(
            productIds,
            (chunk) => supabase
              .from('stock_reservations')
              .select('product_id, quantity')
              .eq('status', 'active')
              .in('product_id', chunk),
          );

          reservations.forEach(r => {
            reservedMap.set(r.product_id, (reservedMap.get(r.product_id) || 0) + r.quantity);
          });
        }

        // Get zone stock for all products
        const zoneStockMap = new Map<string, ProductZoneStock[]>();
        if (productsData) {
          const productIds = productsData.map(p => p.id);
          const zoneStocks = await fetchInChunks<{ product_id: string; quantity: number; zone: unknown }>(
            productIds,
            (chunk) => supabase
              .from('product_zone_storage')
              .select(`
              product_id,
              quantity,
              zone:warehouse_zones(id, name, code)
            `)
              .in('product_id', chunk)
              .gt('quantity', 0),
          );

          zoneStocks.forEach(zs => {
            // zone is a single object from the join
            const zone = zs.zone as unknown as { id: string; name: string; code: string } | null;
            if (!zone) return;

            const existing = zoneStockMap.get(zs.product_id) || [];
            existing.push({
              zone_id: zone.id,
              zone_name: zone.name,
              zone_code: zone.code,
              quantity: zs.quantity
            });
            zoneStockMap.set(zs.product_id, existing);
          });
        }

        // Map products with prices and zones.
        // Fallback: prezzo base del prodotto se non presente nel listino.
        const productsWithPrices: Product[] = (productsData || []).map(p => ({
          id: p.id,
          name: p.name,
          sku: p.sku,
          commercial_name: (p as { commercial_name?: string | null }).commercial_name ?? null,
          stock_quantity: p.stock_quantity,
          reserved_quantity: reservedMap.get(p.id) || 0,
          manage_stock: p.manage_stock ?? true,
          cost_price: p.cost_price || 0,
          vat_rate: (p as { vat_rate?: number | null }).vat_rate ?? null,
          sale_unit: p.order_sale_unit || p.unit_of_measure_1 || 'pz',
          unit_of_measure_1: p.unit_of_measure_1 ?? null,
          pack_weight_kg: p.order_pack_weight_kg ?? null,
          missing_price: !!effectivePriceListId && !priceMap.has(p.id),
          price: priceMap.get(p.id) ?? (p.sale_price ?? p.regular_price ?? 0),
          zones: zoneStockMap.get(p.id) || []
        }));

        if (!cancelled) setProducts(productsWithPrices);
      } catch (error) {
        if (cancelled) return;
        setProducts([]);
        toast.error('Impossibile caricare prodotti e prezzi. Riprova.');
        console.error('Error loading products:', error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadProducts();
    return () => { cancelled = true; };
  }, [organizationId, priceListId]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowProductList(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filter products based on search
  const filteredProducts = products.filter(product => {
    const searchLower = searchTerm.toLowerCase();
    return (
      product.name.toLowerCase().includes(searchLower) ||
      product.sku?.toLowerCase().includes(searchLower) ||
      (showCommercialName && !!product.commercial_name?.toLowerCase().includes(searchLower))
    );
  });

  // Get availability for a product
  const getAvailability = (productId: string): ProductAvailability | undefined => {
    return availabilityResults.find(a => a.product_id === productId);
  };

  // Check if product is already in selected items
  const getSelectedItemIndex = (productId: string): number => {
    return selectedItems.findIndex(item => item.product_id === productId);
  };

  // Net available quantity (stock - prenotazioni attive), stessa base del check RPC allo step Riepilogo
  const getAvailableQuantity = (product: Product): number => {
    return Math.max(0, product.stock_quantity - product.reserved_quantity);
  };

  // Handle clicking on a product - show zone selector if multiple zones
  const handleProductClick = (product: Product) => {
    if (product.missing_price && orderSettings.missing_price === 'block') { toast.error(`${product.name}: prezzo assente nel listino selezionato`); return; }
    // Block if out of stock (solo per prodotti con gestione stock attiva)
    if (product.manage_stock && getAvailableQuantity(product) === 0) {
      toast.error(`${product.name} non è disponibile: quantità esaurita o interamente prenotata`);
      return;
    }

    const existingIndex = getSelectedItemIndex(product.id);

    if (existingIndex >= 0) {
      // Product already exists — use handleQuantityChange to check zone stock
      handleQuantityChange(existingIndex, selectedItems[existingIndex].quantity + 1);
      setSearchTerm('');
      setShowProductList(false);
    } else if (product.manage_stock && product.zones.length > 1) {
      // Multiple zones available - show zone selector
      setSelectedProductForZone(product);
      setShowZoneSelector(true);
      setShowProductList(false);
    } else {
      // Single zone or no zones - add directly
      handleAddProduct(product, product.manage_stock ? product.zones[0] || null : null);
    }
  };

  // Handle adding a product with optional zone
  const handleAddProduct = (product: Product, zone: ProductZoneStock | null) => {
    onAddItem({
      product_id: product.id,
      product_name: product.name,
      product_sku: product.sku,
      sale_unit: product.sale_unit,
      pack_weight_kg: product.pack_weight_kg,
      quantity: 1,
      unit_price: product.price,
      discount_amount: 0,
      tax_rate: product.vat_rate ?? 22,
      zone_id: zone?.zone_id,
      zone_name: zone?.zone_name
    });

    setSearchTerm('');
    setShowProductList(false);
    setShowZoneSelector(false);
    setSelectedProductForZone(null);
  };

  // Handle zone selection (new product or surplus)
  const handleZoneSelect = (zone: ProductZoneStock) => {
    if (surplusContext) {
      const { product, surplusQuantity, excludeZoneIds } = surplusContext;
      const qtyFromThisZone = Math.min(surplusQuantity, zone.quantity);
      const remaining = surplusQuantity - qtyFromThisZone;

      // Find existing item for this product+zone to merge
      const existingIdx = selectedItems.findIndex(
        i => i.product_id === product.id && i.zone_id === zone.zone_id
      );
      if (existingIdx >= 0) {
        onUpdateItem(existingIdx, { quantity: selectedItems[existingIdx].quantity + qtyFromThisZone });
      } else {
        onAddItem({
          product_id: product.id,
          product_name: product.name,
          product_sku: product.sku,
      sale_unit: product.sale_unit,
      pack_weight_kg: product.pack_weight_kg,
          quantity: qtyFromThisZone,
          unit_price: selectedItems.find(i => i.product_id === product.id)?.unit_price ?? 0,
          discount_amount: 0,
          tax_rate: selectedItems.find(i => i.product_id === product.id)?.tax_rate ?? 22,
          zone_id: zone.zone_id,
          zone_name: zone.zone_name
        });
      }

      if (remaining > 0) {
        // Still surplus left — re-open modal excluding used zones
        const newExclude = [...excludeZoneIds, zone.zone_id];
        const stillAvailable = product.zones.filter(
          z => !newExclude.includes(z.zone_id) && z.quantity > 0
        );
        if (stillAvailable.length === 1) {
          // Auto-assign last zone — merge if row exists
          const lastZone = stillAvailable[0];
          const existingLastIdx = selectedItems.findIndex(
            i => i.product_id === product.id && i.zone_id === lastZone.zone_id
          );
          if (existingLastIdx >= 0) {
            onUpdateItem(existingLastIdx, { quantity: selectedItems[existingLastIdx].quantity + remaining });
          } else {
            onAddItem({
              product_id: product.id,
              product_name: product.name,
              product_sku: product.sku,
      sale_unit: product.sale_unit,
      pack_weight_kg: product.pack_weight_kg,
              quantity: remaining,
              unit_price: selectedItems.find(i => i.product_id === product.id)?.unit_price ?? 0,
              discount_amount: 0,
              tax_rate: selectedItems.find(i => i.product_id === product.id)?.tax_rate ?? 22,
              zone_id: lastZone.zone_id,
              zone_name: lastZone.zone_name
            });
          }
          setSurplusContext(null);
          setShowZoneSelector(false);
          setSelectedProductForZone(null);
        } else if (stillAvailable.length > 1) {
          setSurplusContext({ product, surplusQuantity: remaining, excludeZoneIds: newExclude });
        } else {
          // No more zones with stock
          setSurplusContext(null);
          setShowZoneSelector(false);
          setSelectedProductForZone(null);
        }
      } else {
        setSurplusContext(null);
        setShowZoneSelector(false);
        setSelectedProductForZone(null);
      }
    } else if (selectedProductForZone) {
      handleAddProduct(selectedProductForZone, zone);
    }
  };

  // Cancel zone selection
  const handleCancelZoneSelection = () => {
    setShowZoneSelector(false);
    setSelectedProductForZone(null);
    setSurplusContext(null);
  };

  // Handle quantity change — check zone stock for surplus
  const handleQuantityChange = (index: number, newQuantity: number) => {
    if (newQuantity <= 0) {
      onRemoveItem(index);
      return;
    }

    const item = selectedItems[index];
    const product = productsMap.get(item.product_id);

    // If no zone assigned or no product data, just update normally
    if (!item.zone_id || !product || !product.manage_stock || product.zones.length <= 1) {
      onUpdateItem(index, { quantity: newQuantity });
      return;
    }

    // Only check surplus when increasing quantity
    if (newQuantity <= item.quantity) {
      onUpdateItem(index, { quantity: newQuantity });
      return;
    }

    const currentZone = product.zones.find(z => z.zone_id === item.zone_id);
    const zoneStock = currentZone?.quantity ?? 0;

    if (newQuantity <= zoneStock) {
      onUpdateItem(index, { quantity: newQuantity });
    } else {
      // Cap current row to zone max, open selector for surplus
      const cappedQty = Math.min(zoneStock, newQuantity);
      if (cappedQty !== item.quantity) {
        onUpdateItem(index, { quantity: cappedQty });
      }
      const surplus = newQuantity - cappedQty;
      const otherZones = product.zones.filter(z => z.zone_id !== item.zone_id && z.quantity > 0);

      if (otherZones.length === 1) {
        // Only one other zone — merge into existing row or add new
        const targetZone = otherZones[0];
        const existingIdx = selectedItems.findIndex(
          i => i.product_id === product.id && i.zone_id === targetZone.zone_id
        );
        if (existingIdx >= 0) {
          onUpdateItem(existingIdx, { quantity: selectedItems[existingIdx].quantity + surplus });
        } else {
          onAddItem({
            product_id: product.id,
            product_name: product.name,
            product_sku: product.sku,
      sale_unit: product.sale_unit,
      pack_weight_kg: product.pack_weight_kg,
            quantity: surplus,
            unit_price: item.unit_price,
            discount_amount: 0,
            tax_rate: item.tax_rate,
            zone_id: targetZone.zone_id,
            zone_name: targetZone.zone_name
          });
        }
      } else if (otherZones.length > 1) {
        // Multiple zones — open selector
        setSurplusContext({
          product,
          surplusQuantity: surplus,
          excludeZoneIds: [item.zone_id]
        });
        setSelectedProductForZone(product);
        setShowZoneSelector(true);
      }
      // If no other zones with stock, just update to max available
    }
  };

  // Handle price change
  const handlePriceChange = (index: number, newPrice: number) => {
    if (canChangePrice) onUpdateItem(index, { unit_price: newPrice });
  };

  // Calculate line total
  const calculateLineTotal = (item: OrderItemInput): number => {
    return (item.quantity * item.unit_price) - (item.discount_amount || 0);
  };

  return (
    <div className="space-y-4">
      {/* Search input */}
      <div ref={searchRef} className="relative">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setShowProductList(true);
            }}
            onFocus={() => setShowProductList(true)}
            placeholder="Cerca prodotto per nome o SKU..."
            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            disabled={disabled}
          />
        </div>

        {/* Product dropdown */}
        {showProductList && !disabled && (
          <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-64 overflow-auto">
            {loading ? (
              <div className="p-4 text-center text-gray-500 dark:text-gray-400">
                Caricamento prodotti...
              </div>
            ) : filteredProducts.length === 0 ? (
              <div className="p-4 text-center text-gray-500 dark:text-gray-400">
                {searchTerm ? 'Nessun prodotto trovato' : 'Digita per cercare'}
              </div>
            ) : (
              filteredProducts.map(product => {
                const isSelected = getSelectedItemIndex(product.id) >= 0;
                const availableQty = getAvailableQuantity(product);
                const isOutOfStock = product.manage_stock && availableQty === 0;

                return (
                  <div
                    key={product.id}
                    onClick={() => handleProductClick(product)}
                    className={`p-3 border-b border-gray-100 dark:border-gray-700 last:border-b-0 ${
                      isOutOfStock
                        ? 'opacity-50 cursor-not-allowed'
                        : 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700'
                    } ${isSelected ? 'bg-blue-50 dark:bg-blue-900/20' : ''}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center">
                          <Package className="w-5 h-5 text-gray-400" />
                        </div>
                        <div>
                          <div className="font-medium text-gray-900 dark:text-white flex items-center gap-2">
                            {product.name}
                            {isSelected && (
                              <span className="text-xs bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 px-1.5 py-0.5 rounded">
                                Nel carrello
                              </span>
                            )}
                          </div>
                          {showCommercialName && <CommercialNameLine commercialName={product.commercial_name} productName={product.name} />}
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            SKU: {product.sku || 'N/A'}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-medium text-gray-900 dark:text-white">
                          {product.price > 0 ? product.price.toLocaleString('it-IT', { style: 'currency', currency: 'EUR' }) : '-'}
                        </div>
                        <div className={`text-xs ${
                          !product.manage_stock
                            ? 'text-gray-400 dark:text-gray-500'
                            : availableQty > 10
                            ? 'text-green-600 dark:text-green-400'
                            : availableQty > 0
                            ? 'text-yellow-600 dark:text-yellow-400'
                            : 'text-red-600 dark:text-red-400'
                        }`}>
                          {!product.manage_stock
                            ? 'Stock non gestito'
                            : availableQty > 0
                            ? `${formatQuantity(availableQty, { unit: resolveProductUnit(product), unitDisplay: 'none' })} disponibili${product.reserved_quantity > 0 ? ` (${formatQuantity(product.reserved_quantity, { unit: resolveProductUnit(product), unitDisplay: 'none' })} prenotati)` : ''}`
                            : 'Esaurito'}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>

      {/* Riga libera: prodotto non a catalogo, solo per questo ordine.
          Non con il flusso "ordini cliente" del core, che rilegge prodotti e prezzi dal catalogo. */}
      {!orderSettings.enabled && (
        <CustomOrderLineForm organizationId={organizationId} onAddItem={onAddItem} disabled={disabled} />
      )}

      {/* Selected items list */}
      {selectedItems.length > 0 && (
        <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
          {/* Header */}
          <div className="bg-gray-50 dark:bg-gray-800 px-4 py-2 border-b border-gray-200 dark:border-gray-700">
            <div className="grid grid-cols-12 gap-2 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
              <div className="col-span-5">Prodotto</div>
              <div className="col-span-2 text-center">Quantità</div>
              <div className="col-span-2 text-right">Prezzo</div>
              <div className="col-span-2 text-right">Totale</div>
              <div className="col-span-1"></div>
            </div>
          </div>

          {/* Items */}
          <div className="divide-y divide-gray-100 dark:divide-gray-700">
            {selectedItems.map((item, index) => {
              const availability = getAvailability(item.product_id);
              const hasAvailabilityIssue = availability && !availability.is_available;
              const lineTotal = calculateLineTotal(item);
              const product = productsMap.get(item.product_id);
              // Zona della riga con il codice (dalle zone del prodotto caricate); senza, il solo nome salvato
              const itemZone = product?.zones.find(z => z.zone_id === item.zone_id);
              const costPrice = product?.cost_price || 0;
              const marginPct = item.unit_price > 0 && costPrice > 0
                ? ((item.unit_price - costPrice) / item.unit_price) * 100
                : null;

              return (
                <div
                  key={`${item.product_id}-${index}`}
                  className={`px-4 py-3 ${hasAvailabilityIssue ? 'bg-red-50 dark:bg-red-900/10' : ''}`}
                >
                  <div className="grid grid-cols-12 gap-2 items-center">
                    {/* Product info */}
                    <div className="col-span-5">
                      <div className="font-medium text-gray-900 dark:text-white text-sm">
                        {item.product_name}
                      </div>
                      {showCommercialName && (
                        <CommercialNameLine
                          commercialName={products.find(p => p.id === item.product_id)?.commercial_name}
                          productName={item.product_name}
                        />
                      )}
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {isCustomOrderLine(item)
                          ? `Non a catalogo · IVA ${item.tax_rate ?? 0}%`
                          : `SKU: ${item.product_sku || 'N/A'}`}
                        {item.zone_name && (
                          <span className="ml-2 inline-flex items-center gap-1 text-blue-600 dark:text-blue-400">
                            <MapPin className="w-3 h-3" />
                            {formatWarehouseName(itemZone ? { code: itemZone.zone_code, name: itemZone.zone_name } : { name: item.zone_name })}
                          </span>
                        )}
                      </div>
                      {hasAvailabilityIssue && (
                        <div className="flex items-center gap-1 text-xs text-red-600 dark:text-red-400 mt-1">
                          <AlertTriangle className="w-3 h-3" />
                          Disponibili solo {formatQuantity(availability.available_quantity, { unit: resolveProductUnit(product), unitDisplay: 'none' })}
                        </div>
                      )}
                      {costPrice > 0 && (
                        <div className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                          Costo: {costPrice.toLocaleString('it-IT', { style: 'currency', currency: 'EUR' })}
                          {marginPct !== null && (
                            <span className={`ml-2 font-medium ${
                              marginPct >= 20
                                ? 'text-green-600 dark:text-green-400'
                                : marginPct >= 10
                                ? 'text-yellow-600 dark:text-yellow-400'
                                : 'text-red-600 dark:text-red-400'
                            }`}>
                              Margine: {marginPct.toFixed(1)}%
                            </span>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Quantity */}
                    <div className="col-span-2">
                      <div className="flex items-center justify-center gap-1">
                        <button
                          type="button"
                          onClick={() => handleQuantityChange(index, item.quantity - 1)}
                          className="w-7 h-7 flex items-center justify-center rounded border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400"
                          disabled={disabled}
                        >
                          <Minus className="w-3 h-3" />
                        </button>
                        <input
                          type="number"
                          value={item.quantity}
                          onChange={(e) => handleQuantityChange(index, parseInt(e.target.value) || 0)}
                          className="w-14 h-7 text-center border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                          min="0"
                          disabled={disabled}
                        />
                        <button
                          type="button"
                          onClick={() => handleQuantityChange(index, item.quantity + 1)}
                          className="w-7 h-7 flex items-center justify-center rounded border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400"
                          disabled={disabled}
                        >
                          <Plus className="w-3 h-3" />
                        </button>
                      </div>
                    </div>

                    {/* Price */}
                    <div className="col-span-2">
                      <div className="relative">
                        <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-gray-400">€</span>
                        <input
                          type="number"
                          value={item.unit_price}
                          onChange={(e) => handlePriceChange(index, parseFloat(e.target.value) || 0)}
                          className="w-full h-7 pl-5 pr-2 text-right border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                          min="0"
                          step="0.01"
                          disabled={disabled || !canChangePrice}
                        />
                      </div>
                    </div>

                    {/* Line total */}
                    <div className="col-span-2 text-right">
                      <span className="font-medium text-gray-900 dark:text-white text-sm">
                        {lineTotal.toLocaleString('it-IT', { style: 'currency', currency: 'EUR' })}
                      </span>
                    </div>

                    {/* Remove button */}
                    <div className="col-span-1 flex justify-end">
                      <button
                        type="button"
                        onClick={() => onRemoveItem(index)}
                        className="p-1 text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                        disabled={disabled}
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Empty state */}
      {selectedItems.length === 0 && (
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
          <Package className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p className="text-sm">Nessun prodotto selezionato</p>
          <p className="text-xs mt-1">Cerca e aggiungi prodotti all'ordine</p>
        </div>
      )}

      {/* Zone Selection Modal */}
      {showZoneSelector && selectedProductForZone && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {surplusContext
                  ? `Seleziona zona per ${formatQuantity(surplusContext.surplusQuantity, { unit: resolveProductUnit(selectedProductForZone) })} rimanenti`
                  : 'Seleziona Zona di Prelievo'}
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                {selectedProductForZone.name}
              </p>
            </div>
            <div className="p-4 space-y-2 max-h-64 overflow-auto">
              {selectedProductForZone.zones
                .filter(zone => !surplusContext?.excludeZoneIds.includes(zone.zone_id) && zone.quantity > 0)
                .map(zone => (
                <button
                  key={zone.zone_id}
                  onClick={() => handleZoneSelect(zone)}
                  className="w-full p-3 text-left border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-gray-400" />
                      <span className="font-medium text-gray-900 dark:text-white">
                        {formatWarehouseName({ code: zone.zone_code, name: zone.zone_name })}
                      </span>
                    </div>
                    <span className={`text-sm font-medium ${
                      zone.quantity > 10
                        ? 'text-green-600 dark:text-green-400'
                        : zone.quantity > 0
                        ? 'text-yellow-600 dark:text-yellow-400'
                        : 'text-red-600 dark:text-red-400'
                    }`}>
                      {formatQuantity(zone.quantity, { unit: resolveProductUnit(selectedProductForZone) })}
                    </span>
                  </div>
                </button>
              ))}
            </div>
            <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
              <button
                onClick={handleCancelZoneSelection}
                className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
              >
                Annulla
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default OrderProductSelector;
